import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus, Weight, Route as RouteIcon, Trash2, Info } from "lucide-react";
import { Modal } from "../../UI/Modal";
import Button from "../../UI/Button";
import { ServiceLine, ServiceType } from "../../../types/works";
import { TaxType } from "../../../types/service";
import { WORK_SERVICE_TYPES } from "../../../constants/worksConstants";

/** A service captured during this round — either a fill of a planned line or ad-hoc. */
export interface RoundServiceInput {
  lineId?: string;
  serviceTypeId: string;
  name?: string;
  unit?: string;
  unitPrice?: number;
  tax?: TaxType;
  quantity: number;
}

export interface RoundCloseData {
  tonnage: number;
  distanceKm: number;
  services: RoundServiceInput[];
}

interface RoundCloseSheetProps {
  isOpen: boolean;
  roundNumber: number;
  /** Still-open dispatcher-ordered lines the driver can fulfil this round. */
  plannedServices: ServiceLine[];
  /** Ad-hoc services offered — the client's (or General) directory entries;
   *  falls back to the fixed works catalog when none are supplied. */
  serviceOptions?: ServiceType[];
  /** Whether the Services directory has finished loading — gates the empty-state
   *  banner so it doesn't flash before the catalogue arrives. */
  servicesLoaded?: boolean;
  /** Planned mileage per round, shown only as a hint (never prefilled). */
  perRoundDistanceHint?: number;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (data: RoundCloseData) => void;
}

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Round-close capture for cyclic works. The driver enters this round's real
 * tonnage + mileage and records any services performed; on confirm the round is
 * marked unloaded and locked. Tonnage/mileage are required and never prefilled.
 */
export const RoundCloseSheet: React.FC<RoundCloseSheetProps> = ({
  isOpen,
  roundNumber,
  plannedServices,
  serviceOptions,
  servicesLoaded = true,
  perRoundDistanceHint,
  busy,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("works");

  const serviceTypes = serviceOptions ?? WORK_SERVICE_TYPES;
  const findOption = (id?: string) => serviceTypes.find((s) => s.id === id);

  const [tonnage, setTonnage] = useState("");
  const [distance, setDistance] = useState("");
  // Planned lines the driver chooses to fulfil this round → lineId : quantity.
  const [plannedQty, setPlannedQty] = useState<Record<string, string>>({});
  // Ad-hoc services added in this sheet. Price + tax are snapshotted from the
  // chosen directory entry — the driver no longer types a price. MATRIX lines
  // also carry the resolved zone × band price and a describing suffix.
  const [adhoc, setAdhoc] = useState<
    {
      serviceTypeId: string;
      quantity: number;
      unitPrice?: number;
      tax?: TaxType;
      nameSuffix?: string;
    }[]
  >([]);
  const [adding, setAdding] = useState(false);
  const [pickTypeId, setPickTypeId] = useState(serviceTypes[0]?.id ?? "");
  const [pickQty, setPickQty] = useState(1);
  // MATRIX picks: the driver must choose a zone + band to resolve the price.
  const [pickZoneId, setPickZoneId] = useState("");
  const [pickBandId, setPickBandId] = useState("");

  useEffect(() => {
    if (isOpen) {
      setTonnage("");
      setDistance("");
      setPlannedQty({});
      setAdhoc([]);
      setAdding(false);
      setPickTypeId(serviceTypes[0]?.id ?? "");
      setPickQty(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serviceTypes]);

  // Clear the zone/band choice whenever the picked service changes — a stale
  // pair from another service would resolve to the wrong (or no) matrix cell.
  useEffect(() => {
    setPickZoneId("");
    setPickBandId("");
  }, [pickTypeId]);

  const tonnageValid = num(tonnage) > 0;
  const distanceValid = num(distance) > 0;
  const canConfirm = tonnageValid && distanceValid && !busy;

  const services = useMemo<RoundServiceInput[]>(() => {
    const filled: RoundServiceInput[] = [];
    for (const line of plannedServices) {
      const qty = num(plannedQty[line.id] ?? "");
      if (qty > 0) {
        filled.push({ lineId: line.id, serviceTypeId: line.serviceTypeId ?? "", quantity: qty });
      }
    }
    for (const a of adhoc) {
      const svc = findOption(a.serviceTypeId);
      filled.push({
        serviceTypeId: a.serviceTypeId,
        name: a.nameSuffix && svc?.name ? `${svc.name} — ${a.nameSuffix}` : svc?.name,
        unit: svc?.unit,
        unitPrice: a.unitPrice,
        tax: a.tax,
        quantity: a.quantity,
      });
    }
    return filled;
  }, [plannedServices, plannedQty, adhoc]);

  if (!isOpen) return null;

  // Price + tax come from the chosen directory entry. For MATRIX services the
  // price is resolved from the picked zone × band cell (both mandatory); every
  // other type uses the entry's fixed price. The line is addable only when that
  // resolves to a positive price.
  const pickOption = findOption(pickTypeId);
  const pickIsMatrix = pickOption?.pricingType === "MATRIX";
  const pickZones = pickOption?.zones ?? [];
  const pickBands = pickOption?.bands ?? [];
  const pickZone = pickZones.find((z) => z._id === pickZoneId);
  const pickBand = pickBands.find((b) => b._id === pickBandId);
  const pickMatrixCell =
    pickIsMatrix && pickZoneId && pickBandId
      ? pickOption?.priceMatrix?.find((c) => c.zoneId === pickZoneId && c.bandId === pickBandId)
      : undefined;
  const pickPriceNum = pickIsMatrix ? pickMatrixCell?.price ?? 0 : pickOption?.defaultPrice ?? 0;
  /**
   * A resolved price, not a positive one.
   *
   * MATRIX still fails closed: a zone/band pair with no cell in the grid
   * resolves to 0, and that genuinely IS unresolved — there is nothing to
   * charge and nothing the office can look up later. Every other type may
   * legitimately be 0: a free service, or a card with no default rate that the
   * office prices when the work is invoiced (see Service.price). Refusing
   * those made a price-less service impossible to record at all, so the work
   * went unbilled rather than under-billed.
   */
  const pickPriceValid = pickIsMatrix ? pickPriceNum > 0 : pickPriceNum >= 0;
  const pickMatrixReady = !pickIsMatrix || (!!pickZoneId && !!pickBandId);
  const canAddAdhoc = !!pickTypeId && pickQty > 0 && pickMatrixReady && pickPriceValid;

  const confirmAddAdhoc = () => {
    if (!canAddAdhoc) return;
    setAdhoc((list) => [
      ...list,
      {
        serviceTypeId: pickTypeId,
        quantity: pickQty,
        unitPrice: pickPriceNum,
        tax: pickOption?.tax,
        nameSuffix: pickIsMatrix
          ? `${pickZone?.name ?? ""} · ${pickBand?.label ?? ""}`.trim()
          : undefined,
      },
    ]);
    setAdding(false);
    setPickTypeId(serviceTypes[0]?.id ?? "");
    setPickQty(1);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("driver.roundClose.title", { n: roundNumber })} size="sm">
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:p-6 space-y-4">
        <p className="text-xs text-[#8A9590]">{t("driver.roundClose.subtitle")}</p>

        {/* Tonnage */}
        <NumberField
          icon={<Weight size={18} />}
          iconCls="bg-[#FCEEE2] text-[#C97A3C]"
          label={t("driver.roundClose.tonnageLabel")}
          unit="t"
          value={tonnage}
          onChange={setTonnage}
          invalid={tonnage !== "" && !tonnageValid}
        />

        {/* Mileage */}
        <NumberField
          icon={<RouteIcon size={18} />}
          iconCls="bg-[#E7EFFB] text-[#3B6FB6]"
          label={t("driver.roundClose.mileageLabel")}
          unit="km"
          value={distance}
          onChange={setDistance}
          invalid={distance !== "" && !distanceValid}
          hint={
            perRoundDistanceHint
              ? t("driver.roundClose.mileageHint", { km: Math.round(perRoundDistanceHint) })
              : undefined
          }
        />

        {/* Services this round */}
        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590]">
            {t("driver.roundClose.servicesTitle")}
          </div>

          {plannedServices.length === 0 && adhoc.length === 0 && !adding && (
            <p className="text-xs text-[#8A9590]">{t("driver.roundClose.noServices")}</p>
          )}

          {/* Ordered lines awaiting fulfilment */}
          {plannedServices.map((line) => (
            <div
              key={line.id}
              className="flex items-center gap-2 bg-[#FAF9F6] border border-[#ECEAE4] rounded-lg px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#2B2F2E] truncate">{line.name}</div>
                {line.quantityPlanned != null && (
                  <div className="text-[11px] text-[#C97A3C]">
                    {t("driver.exec.ordered", { qty: line.quantityPlanned, unit: line.unit })}
                  </div>
                )}
              </div>
              <input
                type="number"
                min={0}
                step={0.5}
                inputMode="decimal"
                value={plannedQty[line.id] ?? ""}
                onChange={(e) =>
                  setPlannedQty((m) => ({ ...m, [line.id]: e.target.value }))
                }
                placeholder="0"
                className="w-16 text-center text-sm rounded-md border border-[#E5E3DD] py-1.5 focus:outline-none focus:border-[#C97A3C]"
              />
              <span className="text-xs text-[#8A9590] w-8">{line.unit}</span>
            </div>
          ))}

          {/* Ad-hoc lines added in this sheet */}
          {adhoc.map((a, i) => {
            const svc = findOption(a.serviceTypeId);
            return (
              <div
                key={`${a.serviceTypeId}-${i}`}
                className="flex items-center gap-2 bg-[#F4FAF6] border border-[#CDEAD8] rounded-lg px-3 py-2"
              >
                <span className="text-base">{svc?.icon}</span>
                <div className="min-w-0 flex-1 text-sm font-medium text-[#2B2F2E] truncate">
                  {a.nameSuffix ? `${svc?.name} — ${a.nameSuffix}` : svc?.name}
                </div>
                <span className="text-sm font-semibold">
                  {Number.isInteger(a.quantity) ? a.quantity : a.quantity.toFixed(1)} {svc?.unit}
                </span>
                <button
                  type="button"
                  onClick={() => setAdhoc((list) => list.filter((_, idx) => idx !== i))}
                  className="text-[#B0413E] p-1 hover:bg-[#FBE9E8] rounded"
                  aria-label={t("driver.roundClose.remove")}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}

          {/* Inline ad-hoc add */}
          {adding ? (
            <div className="border border-[#ECEAE4] rounded-lg p-3 space-y-3 bg-white">
              {/* Service is chosen from the client's directory; price + tax ride
                  along from that entry, so the driver only sets the quantity. */}
              <select
                value={pickTypeId}
                onChange={(e) => setPickTypeId(e.target.value)}
                className="w-full h-9 px-2.5 text-sm rounded-md border border-[#E5E3DD] bg-white text-[#2B2F2E] focus:outline-none focus:border-[#C97A3C]"
              >
                <option value="">{t("driver.roundClose.selectService")}</option>
                {serviceTypes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.icon} {s.name}
                  </option>
                ))}
              </select>
              {/* MATRIX: zone + band are mandatory to resolve the price. */}
              {pickIsMatrix && (
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={pickZoneId}
                    onChange={(e) => setPickZoneId(e.target.value)}
                    className={`h-9 px-2.5 text-sm rounded-md border bg-white text-[#2B2F2E] focus:outline-none focus:border-[#C97A3C] ${
                      pickZoneId ? "border-[#E5E3DD]" : "border-[#E24B4A]"
                    }`}
                  >
                    <option value="">{t("driver.servicePicker.selectZone")}</option>
                    {pickZones.map((z) => (
                      <option key={z._id} value={z._id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={pickBandId}
                    onChange={(e) => setPickBandId(e.target.value)}
                    className={`h-9 px-2.5 text-sm rounded-md border bg-white text-[#2B2F2E] focus:outline-none focus:border-[#C97A3C] ${
                      pickBandId ? "border-[#E5E3DD]" : "border-[#E24B4A]"
                    }`}
                  >
                    <option value="">{t("driver.servicePicker.selectBand")}</option>
                    {pickBands.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPickQty((q) => Math.max(0.5, Math.round((q - 0.5) * 10) / 10))}
                  className="h-8 w-8 rounded-md border border-[#E5E3DD] flex items-center justify-center text-[#5A6460] hover:border-[#C97A3C]"
                >
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center text-sm font-semibold">
                  {Number.isInteger(pickQty) ? pickQty : pickQty.toFixed(1)}
                </span>
                <button
                  type="button"
                  onClick={() => setPickQty((q) => Math.round((q + 0.5) * 10) / 10)}
                  className="h-8 w-8 rounded-md border border-[#E5E3DD] flex items-center justify-center text-[#5A6460] hover:border-[#C97A3C]"
                >
                  <Plus size={14} />
                </button>
                <span className="ml-2 text-sm font-medium text-[#5A6460] tabular-nums">
                  €{pickPriceNum}
                  {pickOption?.unit ? ` / ${pickOption.unit}` : ""}
                </span>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" onClick={() => setAdding(false)}>
                    {t("form.cancel")}
                  </Button>
                  <Button variant="primary" disabled={!canAddAdhoc} onClick={confirmAddAdhoc}>
                    {t("driver.roundClose.addLine")}
                  </Button>
                </div>
              </div>
              {pickIsMatrix && !pickMatrixReady && (
                <p className="text-[11px] text-[#C9952F]">
                  {t("driver.servicePicker.selectZoneBand")}
                </p>
              )}
              {pickIsMatrix && pickMatrixReady && !pickPriceValid && (
                <p className="text-[11px] text-[#E24B4A]">
                  {t("driver.servicePicker.noMatrixPrice")}
                </p>
              )}
              {/* Informational, not blocking, and keyed on whether the card
                  states a rate rather than on the price being zero — a free
                  service is priced correctly. See ServicePicker. */}
              {!!pickTypeId && !pickIsMatrix && pickOption?.hasRate === false && (
                <p className="text-[11px] text-[#C9952F]">
                  {t("driver.roundClose.noPrice")}
                </p>
              )}
            </div>
          ) : serviceTypes.length > 0 ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#C97A3C] hover:text-[#B86B30]"
            >
              <Plus size={14} /> {t("driver.exec.addService")}
            </button>
          ) : (
            // No catalogue for this work — the main page's banner is hidden
            // behind this modal, so echo it here so the driver knows why there's
            // nothing to add.
            servicesLoaded && (
              <div className="flex items-start gap-2.5 rounded-lg bg-[#EFF4FB] border border-[#CBDCF2] px-3 py-2.5">
                <Info className="h-4 w-4 text-[#3B6FB6] shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-semibold text-[#2B4A73]">
                    {t("driver.exec.noCatalogTitle")}
                  </div>
                  <p className="text-[11px] text-[#3B6FB6] mt-0.5">
                    {t("driver.exec.noCatalogHint")}
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-4 py-3 sm:px-6 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
        <Button variant="outline" onClick={onClose}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={!canConfirm}
          onClick={() => onConfirm({ tonnage: num(tonnage), distanceKm: num(distance), services })}
        >
          {t("driver.roundClose.confirm", { n: roundNumber })}
        </Button>
      </div>
    </Modal>
  );
};

interface NumberFieldProps {
  icon: React.ReactNode;
  iconCls: string;
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  hint?: string;
}

const NumberField: React.FC<NumberFieldProps> = ({ icon, iconCls, label, unit, value, onChange, invalid, hint }) => {
  const { t } = useTranslation("works");
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${iconCls}`}>{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[#8A9590]">{label}</span>
      </div>
      <div
        className={`flex items-stretch rounded-xl border-2 overflow-hidden ${
          invalid ? "border-[#E24B4A]" : "border-[#C97A3C]"
        }`}
      >
        <input
          type="number"
          min={0}
          step="any"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="flex-1 text-center text-2xl font-bold text-[#0B0F0E] bg-transparent py-3 focus:outline-none"
        />
        <div className="px-4 flex items-center bg-[#FAF9F6] border-l border-[#ECEAE4] text-[#8A9590] font-semibold">
          {unit}
        </div>
      </div>
      {invalid ? (
        <p className="mt-1 text-[11px] text-[#E24B4A]">{t("driver.roundClose.invalid")}</p>
      ) : (
        hint && <p className="mt-1 text-[11px] text-[#8A9590]">{hint}</p>
      )}
    </div>
  );
};
